import React, { useEffect, useState } from 'react';
import RichEditor from '../../components/admin/RichEditor';
import { useNavigate, useParams, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import AdminLayout from '../../components/admin/AdminLayout';
import api from '../../utils/api';
import './AdminPageForm.css';

// ══════════════════════════════════════════════════════════
// PAGE FORM
// ══════════════════════════════════════════════════════════
export default function AdminPageForm() {
  const { slug }  = useParams();   // comes from /admin/faqet/ndrysho/:slug
  const navigate  = useNavigate();
  const isEdit    = Boolean(slug);

  const [form, setForm] = useState({
    title: '', slug_field: '', content: '', meta_desc: '', sort_order: 0, show_in_nav: true, is_active: true
  });
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [preview, setPreview] = useState(false);

  useEffect(() => {
    if (!isEdit) { setLoading(false); return; }
    api.get(`/admin/pages/${slug}`)
      .then(res => {
        const p = res.data;
        setForm({
          title:      p.title     || '',
          slug_field: p.slug      || '',
          content:    p.content   || '',
          meta_desc:  p.meta_desc || '',
          sort_order: p.sort_order || 0,
          show_in_nav: p.show_in_nav === 1 || p.show_in_nav === true,
          is_active:  p.is_active === 1 || p.is_active === true,
        });
      })
      .catch(() => toast.error('Gabim gjatë ngarkimit të faqes.'))
      .finally(() => setLoading(false));
  }, [slug, isEdit]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return toast.error('Titulli është i detyrueshëm.');
    setSaving(true);
    try {
      const payload = {
        title:       form.title,
        content:     form.content,
        meta_desc:   form.meta_desc,
        sort_order:  form.sort_order,
        show_in_nav: form.show_in_nav,
        is_active:   form.is_active,
      };
      if (!isEdit) payload.slug = form.slug_field || form.title.toLowerCase().replace(/\s+/g,'-').replace(/[^a-z0-9-]/g,'').slice(0,60);

      if (isEdit) {
        await api.put(`/admin/pages/${slug}`, payload);
        toast.success('Faqja u përditësua me sukses!');
      } else {
        await api.post('/admin/pages', payload);
        toast.success('Faqja u krijua me sukses!');
      }
      navigate('/admin/faqet');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Gabim gjatë ruajtjes.');
    } finally { setSaving(false); }
  };

  if (loading) return <AdminLayout><div className="spinner"/></AdminLayout>;

  return (
    <AdminLayout>
      <div className="admin-page-header">
        <div>
          <h1 className="page-title">{isEdit ? `Ndrysho: ${form.title}` : '📄 Faqe e Re'}</h1>
          <p className="admin-page-sub">
            {isEdit
              ? `Redakto përmbajtjen e faqes "${form.title}"`
              : 'Krijo faqe të re me editor vizual'
            }
          </p>
        </div>
        <div style={{display:'flex',gap:8}}>
          <button type="button" className="btn btn-secondary" onClick={() => setPreview(v => !v)}>
            {preview ? '✏️ Edito' : '👁 Preview'}
          </button>
          <Link to="/admin/faqet" className="btn btn-secondary">← Kthehu</Link>
        </div>
      </div>

      {/* PREVIEW MODE */}
      {preview ? (
        <div className="card page-preview-wrap">
          <div className="page-preview-header">
            <h2>{form.title || 'Pa titull'}</h2>
            <button className="btn btn-primary" onClick={() => setPreview(false)}>← Kthehu tek editimi</button>
          </div>
          <div className="rich-content page-preview-body"
            dangerouslySetInnerHTML={{ __html: form.content || '<p style="color:#9ca3af">Nuk ka përmbajtje ende.</p>' }}
          />
        </div>
      ) : (
        <form onSubmit={handleSubmit}>
          <div className="page-form-grid">
            {/* Main editor */}
            <div>
              <div className="card" style={{padding:'1.25rem',marginBottom:'1rem'}}>
                <div className="form-group" style={{margin:0}}>
                  <label className="form-label">Titulli i faqes *</label>
                  <input
                    className="form-input"
                    required
                    placeholder="p.sh. Historiku i Spitalit..."
                    value={form.title}
                    style={{fontSize:'1rem',fontWeight:600,padding:'10px 14px'}}
                    onChange={e => {
                      const t = e.target.value;
                      setForm(p => ({
                        ...p,
                        title: t,
                        slug_field: !isEdit
                          ? t.toLowerCase().replace(/\s+/g,'-').replace(/[^a-z0-9-]/g,'').slice(0,60)
                          : p.slug_field
                      }));
                    }}
                  />
                </div>
              </div>

              <div className="card" style={{marginBottom:'1rem',overflow:'hidden'}}>
                <div style={{padding:'1rem 1.25rem',borderBottom:'1px solid #eae9e4',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                  <div>
                    <p style={{fontWeight:700,fontSize:'0.875rem',color:'var(--gray-900)'}}>✍️ Përmbajtja</p>
                    <p style={{fontSize:'0.72rem',color:'var(--gray-400)'}}>Përdorni butonat e mësipërm për formatim, foto, video dhe linqe</p>
                  </div>
                </div>
                <RichEditor
                  value={form.content}
                  onChange={content => setForm(p => ({...p, content}))}
                />
              </div>
            </div>

            {/* Sidebar */}
            <div>
              {/* Publish */}
              <div className="card" style={{padding:'1.25rem',marginBottom:'1rem'}}>
                <h3 className="form-side-title">📢 Publiko</h3>
                <label style={{display:'flex',alignItems:'center',gap:8,cursor:'pointer',marginBottom:'1rem',fontSize:'0.875rem',padding:'8px',background:'var(--gray-50)',borderRadius:6}}>
                  <input type="checkbox" checked={form.is_active}
                    onChange={e => setForm(p=>({...p, is_active: e.target.checked}))}/>
                  <span>Faqja aktive (e dukshme)</span>
                </label>
                <button type="submit" className="btn btn-primary" style={{width:'100%',justifyContent:'center',padding:'11px'}} disabled={saving}>
                  {saving ? '⏳ Duke ruajtur...' : isEdit ? '💾 Ruaj ndryshimet' : '✅ Krijo faqen'}
                </button>
              </div>

              {/* URL/Slug */}
              <div className="card" style={{padding:'1.25rem',marginBottom:'1rem'}}>
                <h3 className="form-side-title">🔗 URL e faqes</h3>
                {isEdit ? (
                  <div>
                    <p style={{fontSize:'0.75rem',color:'var(--gray-500)',marginBottom:6}}>Faqja gjendet tek:</p>
                    <code style={{fontSize:'0.78rem',background:'var(--gray-100)',padding:'4px 8px',borderRadius:4,display:'block',wordBreak:'break-all'}}>
                      /faqe/{form.slug_field}
                    </code>
                  </div>
                ) : (
                  <div>
                    <label className="form-label">Slug (gjenerohet automatikisht)</label>
                    <div style={{display:'flex',alignItems:'center',gap:6,background:'var(--gray-50)',border:'1px solid #dddbd6',borderRadius:6,padding:'6px 10px'}}>
                      <span style={{fontSize:'0.75rem',color:'var(--gray-400)',flexShrink:0}}>/faqe/</span>
                      <input
                        style={{border:'none',background:'transparent',fontSize:'0.82rem',flex:1,outline:'none'}}
                        value={form.slug_field}
                        placeholder="url-e-faqes"
                        onChange={e => setForm(p=>({...p, slug_field: e.target.value.toLowerCase().replace(/\s+/g,'-').replace(/[^a-z0-9-]/g,'')}))}
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* SEO */}
              <div className="card" style={{padding:'1.25rem',marginBottom:'1rem'}}>
                <h3 className="form-side-title">🔍 SEO</h3>
                <div className="form-group" style={{margin:0}}>
                  <label className="form-label">Meta përshkrimi</label>
                  <textarea className="form-input" rows={3}
                    placeholder="Përshkrim i shkurtër për Google..."
                    value={form.meta_desc}
                    onChange={e => setForm(p=>({...p, meta_desc: e.target.value}))}/>
                  <p style={{fontSize:'0.68rem',color:'var(--gray-400)',marginTop:4}}>
                    {form.meta_desc.length}/160 karaktere
                  </p>
                </div>
              </div>

              {/* Tips */}
              <div className="card" style={{padding:'1.25rem',background:'#FFFBEB',border:'1px solid #FDE68A'}}>
                <p style={{fontSize:'0.75rem',fontWeight:700,color:'#92400E',marginBottom:8}}>💡 Si të punoni me editorin</p>
                <ul style={{fontSize:'0.72rem',color:'#78350F',lineHeight:1.7,paddingLeft:16}}>
                  <li>Zgjidhni tekstin → klikoni <strong>B/I/U</strong> për formatim</li>
                  <li>Klikoni <strong>🖼</strong> për të ngarkuar foto</li>
                  <li>Klikoni <strong>▶</strong> për video YouTube/Vimeo</li>
                  <li>Klikoni <strong>🔗</strong> për të shtuar link</li>
                  <li>Klikoni <strong>⊞</strong> për tabelë</li>
                  <li>Klikoni <strong>👁 Preview</strong> për të parë rezultatin</li>
                  <li>Klikoni <strong>HTML</strong> për kod të drejtpërdrejtë</li>
                </ul>
              </div>
            </div>
          </div>
        </form>
      )}
    </AdminLayout>
  );
}
